import { useLiveScanToasts } from "@hooks/useLiveScanToasts";
import { WebSocketType } from "@lyteflo/types";
import {
  getWebSocketService,
  initWebSocketService,
  isTeslaGenerationEventDTO,
  refreshTeslaFleet,
  useDeviceSubscription,
  useLiveScanStore,
  useProfileStore,
  useTeslaGenerationCeilingTimer,
  useTeslaGenerationStore,
  useTeslaGenerationSubscription,
} from "@lyteflo/utils";
import { createVisibilityLifecycleAdapter } from "@services/websocket/visibilityLifecycleAdapter";
import { type ReactNode, useEffect, useMemo, useRef } from "preact/compat";

interface WebSocketProviderProps {
  children: ReactNode;
}

export const WebSocketProvider = ({ children }: WebSocketProviderProps) => {
  const isAuthenticated = useProfileStore((state) => state.isAuthenticated);

  useLiveScanToasts();
  useDeviceSubscription();
  useTeslaGenerationSubscription();
  useTeslaGenerationCeilingTimer();

  useMemo(() => {
    initWebSocketService({
      authApiUrl: `${import.meta.env.VITE_API_BASE_URL}${import.meta.env.VITE_API_AUTH}`,
      wsUrl: `${import.meta.env.VITE_WS_BASE_URL}${import.meta.env.VITE_WS_PATH}`,
      lifecycleAdapter: createVisibilityLifecycleAdapter(),
      disconnectOnBackground: false,
    });
  }, []);

  const webSocketService = getWebSocketService();
  const isInitialOpenRef = useRef(true);

  useEffect(() => {
    if (isAuthenticated) {
      const init = async () => {
        try {
          await webSocketService.connect();
        } catch (error) {
          console.error("Failed to initialize WebSocket connection:", error);
        }
      };

      void init();

      return () => {
        webSocketService.disconnect(1000, "ws-provider-unmount");
      };
    } else {
      webSocketService.unsubscribeFromDevices();
      useLiveScanStore.getState().clearLiveScans();
      webSocketService.disconnect(1000, "auth-logout");
    }
  }, [isAuthenticated, webSocketService]);

  useEffect(() => {
    const off = webSocketService.setRouter({
      [WebSocketType.DEVICE]: (eventId, payload) => {
        useLiveScanStore.getState().addLiveScan(eventId, payload as never);
      },
      [WebSocketType.DEVICE_UPDATE]: (eventId, payload) => {
        useLiveScanStore
          .getState()
          .applyFirmwareUpdate(eventId, payload as never);
      },
      [WebSocketType.TESLA]: (_eventId, payload) => {
        if (!isTeslaGenerationEventDTO(payload)) return;
        useTeslaGenerationStore.getState().applyGenerationEvent(payload);
      },
    });
    return () => {
      off();
    };
  }, [webSocketService]);

  useEffect(() => {
    if (!isAuthenticated) return;
    const off = webSocketService.onOpen(() => {
      if (isInitialOpenRef.current) {
        isInitialOpenRef.current = false;
        return;
      }
      void useLiveScanStore.getState().refreshLiveScans();
      // Pushes were lost with the socket, so no run state is trusted until a read answers.
      refreshTeslaFleet({ runStateUnknown: true });
    });
    return () => {
      off();
      isInitialOpenRef.current = true;
    };
  }, [isAuthenticated, webSocketService]);

  return children;
};
